import { useState } from 'react';
import { attributeWrite, branchFor, COMPONENT_VISIBILITIES, computeBaseValue, defaultUnitId, emptyAttribute, numericFrom, showsNotesAndVisibility, Size, t, unitChoices, type AttributeDraft, type AttributeHost, type AttributeTypeRow, type AttributeTypeValueRow, type AttributeWrite, type ComponentVisibility, type UnitRow } from '@bms2/shared';
import { NumericInput, SegmentedTab, Select, Textarea } from '../controls';
import { Modal } from './Modal';
import styles from './modal.module.css';

/**
 * The Add/Edit Attribute modal (piece 5; decisions 0165, 0167). One modal
 * serves both hosts — an `ItemAttribute` on an Item and a `ComponentAttribute`
 * on an `AssetNode` — because the two records differ only in which parent they
 * hang off and in their column shape. The parent is filled from the context
 * that opened the modal and locked, so no run lets a person repoint the
 * attribute at another host.
 *
 * The modal reopens on the originally typed value, not the record page's
 * display value: a value entered as 3.7 kW displays as 5 HP on the page, and
 * the modal is the only place the typed value is visible at all.
 */
export const attributeModalDocumentedStates = ['measuredItem', 'measuredAssetNode', 'enumerated'] as const;

export interface AttributeModalProps {
  open: boolean;
  host: AttributeHost;
  /** The Item or AssetNode the attribute hangs off, from the opening context. */
  parentId: string;
  subtitle?: string;
  attributeTypes: readonly AttributeTypeRow[];
  units: readonly UnitRow[];
  attributeTypeValues: readonly AttributeTypeValueRow[];
  initial?: AttributeDraft;
  onSave: (write: AttributeWrite) => void;
  onCancel: () => void;
  openerId?: string | null;
}

export function AttributeModal({
  open,
  host,
  parentId,
  subtitle,
  attributeTypes,
  units,
  attributeTypeValues,
  initial,
  onSave,
  onCancel,
  openerId = null,
}: AttributeModalProps) {
  const [draft, setDraft] = useState<AttributeDraft>(initial ?? emptyAttribute(host, parentId));
  const type = attributeTypes.find((a) => a.id === draft.attributeTypeId) ?? null;
  const measured = type ? branchFor(type.kind) === 'measured' : false;
  const choices = type ? unitChoices(units, type) : [];
  const unit = units.find((u) => u.id === draft.unitId) ?? null;
  const values = type ? attributeTypeValues.filter((v) => v.attributeTypeId === type.id) : [];
  const ready = Boolean(type) && (measured ? draft.enteredValue.trim() !== '' : draft.attributeTypeValueId !== null);

  const pickType = (id: string) => {
    const next = attributeTypes.find((a) => a.id === id) ?? null;
    setDraft((d) => ({
      ...d,
      attributeTypeId: next?.id ?? null,
      // The unit family is derived and never asked; the unit preselects the
      // type's default, so choosing Motor Rating opens on MechanicalHorsepower.
      unitId: next ? defaultUnitId(next) : null,
      attributeTypeValueId: null,
    }));
  };

  const save = () => {
    if (!type || !ready) return;
    onSave(attributeWrite(draft, type, measured ? unit : null));
    onCancel();
  };

  const modalProps = {
    open,
    title: t('attribute.title'),
    onCancel,
    openerId,
    dataModal: 'attribute',
    actions: [
      { kind: 'primary' as const, label: t('attribute.save'), onAct: save, disabled: !ready },
      { kind: 'cancel' as const, label: t('modal.cancel'), onAct: onCancel },
    ],
    ...(subtitle === undefined ? {} : { subtitle }),
  };

  return (
    <Modal {...modalProps}>
      {/* The parent is shown and never offered as a choice. */}
      <p className={styles.readOnly} data-attribute-parent={draft.parentId} data-attribute-host={draft.host}>
        {t('attribute.parent')} {draft.parentId}
      </p>

      {/* 🔒 The Select control, as every fixed set of choices is (decisions 0633, 0969). */}
      <Select size={Size.Medium}
        label={t('attribute.type')}
        required
        value={draft.attributeTypeId ?? ''}
        onChange={pickType}
        placeholder={t('note.none')}
        options={attributeTypes.map((row) => ({ value: row.id, label: row.attributeName }))}
        dataAttribute={{ 'data-attribute-field': 'type' }}
      />

      {type && measured ? (
        <>
          {/* A measured value is a number and types as one (decision 0297); the draft keeps it as the text the save parses. */}
          <NumericInput size={Size.Small} label={t('attribute.value')} kind="number" value={draft.enteredValue.trim() !== '' && Number.isFinite(Number(draft.enteredValue)) ? Number(draft.enteredValue) : null} onChange={(v) => setDraft((d) => ({ ...d, enteredValue: v === null ? '' : String(v) }))} required />
          <Select size={Size.Medium}
            label={t('attribute.unit')}
            value={draft.unitId ?? ''}
            onChange={(next) => setDraft((d) => ({ ...d, unitId: next === '' ? null : next }))}
            options={choices.map((row) => ({ value: row.id, label: row.unitName }))}
            dataAttribute={{ 'data-attribute-field': 'unit' }}
          />
          {/* baseValue is never a form field; it is computed on save and shown. */}
          <p className={styles.fieldLabel} data-attribute-base={String(computeBaseValue(numericFrom(draft.enteredValue), unit) ?? '')}>
            {t('attribute.base')} {computeBaseValue(numericFrom(draft.enteredValue), unit) ?? '—'}
          </p>
        </>
      ) : null}

      {type && !measured ? (
        <Select size={Size.Medium}
          label={t('attribute.value')}
          required
          value={draft.attributeTypeValueId ?? ''}
          onChange={(next) => setDraft((d) => ({ ...d, attributeTypeValueId: next === '' ? null : next }))}
          placeholder={t('note.none')}
          options={values.map((row) => ({ value: row.id, label: row.value }))}
          dataAttribute={{ 'data-attribute-field': 'enumerated' }}
        />
      ) : null}

      {showsNotesAndVisibility(draft.host) ? (
        <>
          <Textarea size={Size.Large} label={t('attribute.notes')} value={draft.notes ?? ''} onChange={(v) => setDraft((d) => ({ ...d, notes: v === '' ? null : v }))} />
          <SegmentedTab
            name="attribute-visibility"
            legend={t('attribute.visibility')}
            segments={COMPONENT_VISIBILITIES.map((v) => ({ value: v, label: v }))}
            value={draft.visibility}
            onChange={(v) => setDraft((d) => ({ ...d, visibility: v as ComponentVisibility }))}
          />
        </>
      ) : null}
    </Modal>
  );
}
